import React, { useState } from 'react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { PROGRAM_TYPE_COLORS } from '../../Program/components/learning-lifecycle/constants';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { updateSelectedId } from '../../../../../redux/slices/selectedGroupSlice';
import { setSelectedValue } from '../../../../../redux/slices/selectedValueSlice';
import { fetchMentorTasks } from '../../../../../redux/slices/mentorTasksSlice';
import { groupRoleListUpdate } from '../../../../../redux/slices/groupRolesListSlice';
import { API } from '../../../../../api';
import ProgramNameText from '../../../../../components/ui/ProgramNameText';
import ProgramKindIcon from '../../../../../components/ui/ProgramKindIcon';
import DosenTooltip from '../../../../../components/ui/DosenTooltip';
import InfoTip from '../../../../../components/ui/InfoTip';
import { formatDate, fromEpochSeconds } from '../../../../../utils/formatDate';
import { isDueUrgent } from '../../../../../components/gamification/dueUrgency';
import type { MemberProgramCardData, MemberProgramStatus, MemberRoleKey } from './types';
import { entryForProgram, withEntries } from './roleListSync';
import { useMemberWord } from '../../../../../utils/memberTypeLabels';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

interface Props {
  program: MemberProgramCardData;
  // Set by the LL Member dashboard host — opens the legacy expanded
  // drawer for this program instead of navigating. The role passed back
  // is the one currently selected on the Mentor/Learner toggle.
  onContinue?: (role: MemberRoleKey) => void;
  // True when this program is in the Urgent Zone of the gamification hub's
  // Programs Due flyout — drives the red "!" beside the CTA and the red outline.
  urgent?: boolean;
}

// Tiered progress bar fill (matches Admin spec):
//   ≥ 70%  green   #00972D
//   30–69% amber   #F0B80D
//   < 30%  orange  #F79420
const PROGRESS_TRACK_BG = '#2A2A2A';
const getProgressFill = (rate: number): string => {
  if (rate >= 70) return '#00972D';
  if (rate >= 30) return '#F0B80D';
  return '#F79420';
};

// Amber "Not Started" status text (same hex as the 30–69% bar tier).
const STATUS_NOT_STARTED_COLOR = '#F0B80D';

// PRD Q5 — red Due By on a non-completed program in the urgent window, the
// same rule as the Urgent Zone and the "!".
const DUE_BY_RED = '#DA2828';
const getDueByColor = (status: MemberProgramStatus, dueBy: number | null): string | undefined => {
  if (dueBy === null) return undefined;
  if (status === 'completed') return undefined; // completed programs never flag
  return isDueUrgent(dueBy, Math.floor(Date.now() / 1000)) ? DUE_BY_RED : undefined;
};

// "in-progress" → "In progress", "not-started" → "Not started" (sentence case on screen)
const formatStatus = (s: MemberProgramStatus): string => {
  const words = s.split('-').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// "masterclass" → "Course", "guided-masterclass" → "Guided Course"
const MODEL_DISPLAY_LABELS: Record<string, string> = {
  masterclass: 'Course',
  'guided-masterclass': 'Guided Course',
  mentorship: 'Mentorship'
};
const formatModel = (m: string): string =>
  MODEL_DISPLAY_LABELS[m] ||
  m
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// unix seconds → "June 30th"
const formatDueBy = (unix: number): string => {
  const d = new Date(unix * 1000);
  const month = d.toLocaleString('en-US', { month: 'long' });
  const day = d.getDate();
  const mod100 = day % 100;
  const mod10 = day % 10;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th' : mod10 === 1 ? 'st' : mod10 === 2 ? 'nd' : mod10 === 3 ? 'rd' : 'th';
  return `${month} ${day}${suffix}`;
};

// unix seconds → "Jun 30, 2026" (the house date format)
const formatDueDate = (unix: number): string => formatDate(fromEpochSeconds(unix), 'Not set');

// minutes → "0h 43m" (e.g. 43 → "0h 43m", 90 → "1h 30m", 60 → "1h 0m")
const formatTimeRemaining = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
};

// Numeric metric → string ("None" when null/undefined).
const formatMetric = (n: number | null | undefined): string => (n == null ? 'None' : String(n));

const PROGRAM_COMPLETION_TOOLTIP = 'Your progress on this program.';

/**
 * Completion dial — the card's headline figure when collapsed, matching the
 * admin Learning Framework card. Same colour tiers as the expanded progress
 * bar, so a card reads the same either way.
 */
const CompletionDial: React.FC<{ rate: number; size?: number }> = ({ rate, size = 68 }) => {
  const clamped = Math.min(100, Math.max(0, Math.round(rate)));
  const stroke = size < 68 ? 5 : 6;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <DosenTooltip title={PROGRAM_COMPLETION_TOOLTIP} placement="bottom">
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, cursor: 'default' }}>
        <Box component="svg" width={size} height={size} sx={{ display: 'block', transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={PROGRESS_TRACK_BG} strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={getProgressFill(clamped)}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped / 100)}
            style={{ transition: 'stroke-dashoffset 0.3s' }}
          />
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: size < 68 ? 13 : 16,
              fontWeight: 700,
              color: '#FDFDFD',
              lineHeight: 1
            }}
          >
            {clamped}%
          </Typography>
        </Box>
      </Box>
    </DosenTooltip>
  );
};

const PROGRAM_LOCKED_TOOLTIP = 'Your program owner will unlock this program when it is ready for completion.';

/**
 * Member-side program card. Branches internally on (model, role) to render
 * one of the five layouts defined in the PRD:
 *   L1 Masterclass            ← Step 4
 *   L2 Mentorship Mentor      ← Step 5
 *   L3 Mentorship Learner     ← Step 5
 *   L4 Guided MC Mentor       ← Step 6 (this commit)
 *   L5 Guided MC Learner      ← Step 6 (this commit) — fields mirror L1
 *
 * Mentorship and Guided Masterclass cards always render the role chip
 * (PRD Q7). Single-role programs show a static chip in the right slot;
 * dual-role programs swap it for an interactive Mentor/Learner toggle.
 *
 * Field lists compose from three shared blocks:
 *   • MasterclassFields        Status / Due By / MCQ Score / Remaining
 *   • MentorshipLearnerFields  Status / Mentors Engaged / Meetings… / Future…
 *   • MentorFields             Learners… / Messages… / Meetings… / Future…
 *
 * Composition per layout:
 *   L1 Masterclass             → MasterclassFields
 *   L3 Mentorship Learner      → MentorshipLearnerFields
 *   L2 Mentorship Mentor       → MentorFields
 *   L5 Guided MC Learner       → MasterclassFields (per Cian's spec)
 *   L4 Guided MC Mentor        → MentorFields (per Cian's spec)
 *
 * Read-only: no inline editors, no Add CTAs. Continue → navigates to the
 * legacy program drawer for now (PRD Q3: "same as today's portfolio").
 */
const MemberProgramCard: React.FC<Props> = ({ program, onContinue, urgent }) => {
  const isMobileApp = useIsMobileApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const groupRoleEntries = useAppSelector((state) => state.groupRoleList?.data || []);

  // Mentor/Learner toggle state — only meaningful when roles.length > 1.
  // For single-role programs the toggle hides; viewRole still drives the
  // field list so the card renders the right metrics.
  const [viewRole, setViewRole] = useState<MemberRoleKey>(program.roles[0] ?? 'mentee');

  // Continue → opens the legacy expanded drawer (leaderboard, peers,
  // tasks, etc.) for this program. The MemberPortfolioSwitch detects
  // location.state.openProgramId and renders UserProgramsDashboard so
  // that dashboard's auto-open effect (line ~822) takes over and pops
  // the drawer. The drawer's own "Continue Program" button then handles
  // the mentee/mentor route navigation + Redux sync.
  const handleContinue = () => {
    if (program.locked) return; // locked program — CTAs are disabled (defense)
    // Redux pre-sync so when the drawer's Continue handles the redirect, state
    // already matches the role the user picked here. A learner enrolled moments
    // ago may not be in the role list yet (roleListSync.ts): the card already
    // knows the program, and the list takes the entry too.
    const { entry, known } = entryForProgram(groupRoleEntries, program, viewRole);
    if (!known) dispatch(groupRoleListUpdate.update(withEntries(groupRoleEntries, [entry])));
    dispatch(updateSelectedId(entry));
    dispatch(setSelectedValue(`${entry.id}_${entry.role}`));
    try {
      localStorage.setItem('lastseen', JSON.stringify(entry));
    } catch {
      /* localStorage full — non-fatal */
    }
    if (entry.role === 'mentor') {
      dispatch(fetchMentorTasks({ groupId: entry.id }));
    }
    if (onContinue) {
      // LL dashboard host — opens the expanded drawer in place.
      onContinue(viewRole);
      return;
    }
    // Standalone render (no host) — fall back to the navigate-and-let-
    // legacy-auto-open path.
    navigate('/app/home/user', {
      state: {
        openProgramId: program.id,
        preferredRole: viewRole
      }
    });
  };

  // Improve Performance CTA (L-11/L-14): sync Redux for the mentee role and
  // route straight to the earliest outstanding item's lesson in retake mode.
  const handleImprove = async () => {
    if (program.locked) return; // locked program — CTAs are disabled (defense)
    // A learner enrolled moments ago may not be in the role list yet
    // (roleListSync.ts). Skipping the sync and navigating anyway opened the
    // lesson page with no program; the card already knows it, and the list
    // takes the entry too.
    const { entry, known } = entryForProgram(groupRoleEntries, program, 'mentee');
    if (!known) dispatch(groupRoleListUpdate.update(withEntries(groupRoleEntries, [entry])));
    dispatch(updateSelectedId(entry));
    dispatch(setSelectedValue(`${entry.id}_${entry.role}`));
    try {
      localStorage.setItem('lastseen', JSON.stringify(entry));
    } catch {
      /* localStorage full — non-fatal */
    }
    try {
      const res = await API.getElliottImproveQueue({ groupId: program.id });
      const item = (res.data?.items || [])[0];
      if (item) {
        navigate(`/app/home/mentee/level/${item.levelId}/task/${item.taskId}?improve=1`);
        return;
      }
    } catch {
      /* queue unavailable — fall back to the normal Continue path */
    }
    handleContinue();
  };
  // Lessons only exist for the learner side: masterclass (no roles) or any
  // membership that includes mentee.
  // A locked program offers no Improve CTA at all — the BE rejects the
  // improve/retake endpoints for learners while the lock is on, so showing
  // even a greyed CTA advertises an action that can't happen. Locked cards
  // fall back to the disabled Continue, which carries the lock affordance.
  const isLearner = program.roles.length === 0 || program.roles.includes('mentee' as MemberRoleKey);
  const onImprove = program.unresolvedLessons > 0 && isLearner && program.locked !== true ? handleImprove : undefined;

  const roleChip = <MentorshipToggle roles={program.roles} value={viewRole} onChange={setViewRole} />;

  const completed = program.status === 'completed';
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Collapsed by default — name, completion dial and the CTA only, matching
  // the admin Learning Framework card. Everything else lives behind the
  // expander so four columns of cards stay scannable.
  const fieldsFor = (model: string) => {
    if (model === 'masterclass') return <MasterclassFields program={program} />;
    if (model === 'mentorship')
      return viewRole === 'mentee' ? <MentorshipMenteeFields program={program} /> : <MentorFields program={program} />;
    return viewRole === 'mentee' ? <MasterclassFields program={program} /> : <MentorFields program={program} />;
  };

  const supportedModel =
    program.model === 'masterclass' || program.model === 'mentorship' || program.model === 'guided-masterclass';

  // Phone: the same card, full width and shorter. The name pill on top with
  // the name in full; under it the dial, the status, the CTA and the expander
  // in one row, so the card's action is one tap away without a tall column.
  if (supportedModel && isMobileApp) {
    const locked = program.locked === true;
    return (
      <CardWrapper completed={completed} urgent={urgent}>
        <NamePill program={program} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CompletionDial rate={program.goalProgress} size={52} />
          {/* The card's own Status (and Due by), out of the details; a tap here opens the rest. */}
          <Box
            onClick={() => setDetailsOpen((o) => !o)}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer'
            }}
          >
            {urgent && <UrgentMark />}
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, lineHeight: '18px', color: '#868686' }}>
              {program.id.startsWith('assessment:') ? 'Assessment' : formatModel(program.model)}
              {' · '}
              <Box
                component="span"
                sx={{
                  color: program.status === 'not-started' ? STATUS_NOT_STARTED_COLOR : '#B6B6B6',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatStatus(program.status)}
              </Box>
              {program.dueBy !== null && (
                <>
                  {' · '}
                  <Box
                    component="span"
                    sx={{ color: getDueByColor(program.status, program.dueBy) ?? '#868686', whiteSpace: 'nowrap' }}
                  >
                    Due {formatDueBy(program.dueBy)}
                  </Box>
                </>
              )}
            </Typography>
          </Box>
          {onImprove ? (
            <RetakesLink onClick={onImprove} />
          ) : (
            <ContinueLink onClick={handleContinue} disabled={locked} />
          )}
          <IconButton
            onClick={() => setDetailsOpen((o) => !o)}
            aria-label={detailsOpen ? `Hide details for ${program.name}` : `Show details for ${program.name}`}
            aria-expanded={detailsOpen}
            sx={{ width: 40, height: 40, mr: '-8px', p: 0, color: '#B6B6B6', flexShrink: 0 }}
          >
            {detailsOpen ? <ExpandLessIcon sx={{ fontSize: 22 }} /> : <ExpandMoreIcon sx={{ fontSize: 22 }} />}
          </IconButton>
        </Box>

        <Collapse in={detailsOpen} sx={{ '&.MuiCollapse-hidden': { display: 'none' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldRow
                label="Program:"
                value={formatModel(program.model)}
                trailing={program.model === 'masterclass' ? undefined : roleChip}
              />
              {fieldsFor(program.model)}
              <FieldRow label="Owner:" value={program.owner?.name || 'Not set'} />
            </Box>
            <ProgressRow rate={program.goalProgress} />
          </Box>
        </Collapse>
      </CardWrapper>
    );
  }

  if (supportedModel) {
    return (
      <CardWrapper completed={completed} urgent={urgent}>
        <NamePill program={program} />

        {/* Dial stays mounted whether or not the details are open — tearing it
            out on expand is what made the card jump a frame. */}
        {/* 8px of margin on top of the wrapper's 8px gap gives the dial an even
            16px above and below, in both the collapsed and expanded states. */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: '8px' }}>
          <CompletionDial rate={program.goalProgress} />
        </Box>

        {/* Kept mounted so only the details animate, but taken out of the flex
            flow once fully closed — a zero-height Collapse still counted as a
            child, which put two 8px gaps below the dial against one above it. */}
        <Collapse in={detailsOpen} sx={{ '&.MuiCollapse-hidden': { display: 'none' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <FieldRow
                label="Program:"
                value={formatModel(program.model)}
                trailing={program.model === 'masterclass' ? undefined : roleChip}
              />
              {fieldsFor(program.model)}
              {/* Owner moved in here — the footer slot it used to hold now
                  carries the learner's CTA. */}
              <FieldRow label="Owner:" value={program.owner?.name || 'Not set'} />
            </Box>
            <ProgressRow rate={program.goalProgress} />
          </Box>
        </Collapse>

        <Divider />
        <CardFooter
          onContinue={handleContinue}
          onImprove={onImprove}
          locked={program.locked === true}
          detailsOpen={detailsOpen}
          onToggleDetails={() => setDetailsOpen((o) => !o)}
          programName={program.name}
          urgent={urgent}
          dueBy={program.dueBy}
        />
      </CardWrapper>
    );
  }

  // Unrecognised model — render a dashed placeholder so the card still
  // appears in the column rather than silently dropping out.
  return (
    <Box
      sx={{
        bgcolor: '#151515',
        border: '1px dashed #2A2A2A',
        borderRadius: '6px',
        p: '15px',
        color: '#868686',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13
      }}
    >
      {program.name}
    </Box>
  );
};

// ─── Field-list blocks ──────────────────────────────────────────────────
// Three reusable groups; cards compose them by model+role (see header doc).

// Masterclass-style fields (also used by Guided MC Learner).
const MasterclassFields: React.FC<{ program: MemberProgramCardData }> = ({ program }) => (
  <>
    <FieldRow
      label="Status:"
      value={formatStatus(program.status)}
      valueColor={program.status === 'not-started' ? STATUS_NOT_STARTED_COLOR : undefined}
    />
    <FieldRow
      label="Due by:"
      value={program.dueBy != null ? formatDueDate(program.dueBy) : 'Not set'}
      valueColor={getDueByColor(program.status, program.dueBy)}
    />
    <FieldRow label="Quiz score:" value={program.mcqScore != null ? `${program.mcqScore}%` : 'None'} />
    <FieldRow
      label="Remaining:"
      value={program.timeRemainingMinutes != null ? formatTimeRemaining(program.timeRemainingMinutes) : 'Not set'}
    />
  </>
);

// Mentorship Learner fields (no Messages Sent; relationship-centric).
const MentorshipMenteeFields: React.FC<{ program: MemberProgramCardData }> = ({ program }) => {
  const w = useMemberWord();
  return (
    <>
      <FieldRow
        label="Status:"
        value={formatStatus(program.status)}
        valueColor={program.status === 'not-started' ? STATUS_NOT_STARTED_COLOR : undefined}
      />
      <FieldRow label={`${w('mentor', { plural: true })} engaged:`} value={formatMetric(program.mentorsEngaged)} />
      <FieldRow label="Meetings completed:" value={formatMetric(program.meetingsCompleted)} />
      <FieldRow label="Future meetings:" value={formatMetric(program.futureMeetings)} />
    </>
  );
};

// Mentor fields — shared by Mentorship Mentor (L2) and Guided MC Mentor
// (L4). Both surface the same coaching metrics regardless of model.
const MentorFields: React.FC<{ program: MemberProgramCardData }> = ({ program }) => (
  <>
    <FieldRow label="Learners engaged:" value={formatMetric(program.menteesEngaged)} />
    <FieldRow label="Messages sent:" value={formatMetric(program.messagesSent)} />
    <FieldRow label="Meetings completed:" value={formatMetric(program.meetingsCompleted)} />
    <FieldRow label="Future meetings:" value={formatMetric(program.futureMeetings)} />
  </>
);

// ─── Shared layout primitives ───────────────────────────────────────────

// Card wrapper — every layout shares this outer shell. Padding + gap
// tuned in Step 4 to land three cards per column without scroll.
// completed=true applies a muted overlay so finished programs are
// visually de-emphasised while remaining readable (Step 8).
// urgent=true outlines the card in red, alongside the footer's "!".
const CardWrapper: React.FC<{ children: React.ReactNode; completed?: boolean; urgent?: boolean }> = ({
  children,
  completed,
  urgent
}) => (
  <Box
    sx={{
      // Lift + soft shadow — matches the admin Learning Framework tiles: a
      // lighter fill above the near-black page, brighter border and drop shadow
      // so each program card reads as a distinct, raised object.
      bgcolor: '#1E1E1E',
      border: `1px solid ${urgent ? DUE_BY_RED : '#555555'}`,
      borderRadius: '6px',
      boxShadow: '0 6px 16px -8px rgba(0,0,0,0.85)',
      p: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      [MOBILE_SX]: { p: '12px', borderRadius: '10px', borderColor: urgent ? DUE_BY_RED : '#3A3A3A' }
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ...(completed && { opacity: 0.45 }) }}>
      {children}
    </Box>
    {/* A "Refresher due · 3 questions · 2 min" row used to sit here on every
        completed card, from a hardcoded constant. It returns with the BE
        refresher endpoints that can say what this learner actually owes. */}
  </Box>
);

// Name pill — centred, with a min-width baseline matching the
// "Permissions and in-Design" pill width. Longer names expand beyond.
// The baseline is clamped to the card: when min-width and max-width conflict,
// CSS resolves in favour of min-width, so a flat `minWidth: 260` beat
// `maxWidth: '100%'` and pushed the pill 4px past each edge of a 252px card.
// That overflow is what put a horizontal scrollbar on the column.
// A locked program carries the Locked badge here as well as the footer
// lock, so the state reads from the card title rather than only from the
// greyed-out CTA.
const NamePill: React.FC<{ program: MemberProgramCardData }> = ({ program }) => {
  const typeColor = PROGRAM_TYPE_COLORS[program.programType];
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          minWidth: 'min(260px, 100%)',
          maxWidth: '100%',
          bgcolor: '#2A2A2A',
          border: '1px solid #444444',
          borderRadius: '4px',
          pt: '6px',
          pb: '6px',
          pl: '8px',
          pr: '8px',
          // Phone: the pill spans the card and the name wraps rather than cutting off.
          [MOBILE_SX]: {
            width: '100%',
            justifyContent: 'flex-start',
            py: '8px',
            px: '10px',
            '& .MuiTypography-root': { whiteSpace: 'normal', maxWidth: 'none', overflowWrap: 'break-word' }
          }
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '9999px', bgcolor: typeColor, flex: '0 0 auto' }} />
        <ProgramNameText name={program.name} locked={program.locked === true} />
        {/* What kind of program it is: an Assessment (its id says so) or a Training. Mentorship has neither. */}
        {program.id.startsWith('assessment:') ? (
          <ProgramKindIcon kind="assessment" />
        ) : (
          program.model !== 'mentorship' && <ProgramKindIcon kind="training" />
        )}
        {/* Padlock sits inline at the end of the pill (same as the admin card):
            floating it in the card corner either overlapped the name or stole
            width from every card, locked or not. The name greys out too. */}
        {program.locked === true && (
          <DosenTooltip title={PROGRAM_LOCKED_TOOLTIP} placement="bottom">
            <LockOutlinedIcon sx={{ flexShrink: 0, fontSize: 15, color: '#F79420', cursor: 'default' }} />
          </DosenTooltip>
        )}
      </Box>
    </Box>
  );
};

// Thin divider — 1px between the details and the footer.
const Divider: React.FC = () => <Box sx={{ height: '1px', bgcolor: '#2A2A2A' }} />;

// Footer — the learner's CTA with the details expander immediately to its
// right, centred (the admin card's owner+expander pairing, with the CTA in
// the owner's place). Owner moved into the expanded field list.
//
// Improve Performance REPLACES Continue rather than sitting beside it: the
// learner has already worked through the lessons the CTA points at, so
// "Continue" has nothing left to offer them and two competing CTAs on one
// card just split the action. Improve itself routes into the earliest
// outstanding lesson (retake mode), so nothing is unreachable.
const CardFooter: React.FC<{
  onContinue: () => void;
  onImprove?: () => void;
  locked?: boolean;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  programName: string;
  urgent?: boolean;
  dueBy: number | null;
}> = ({ onContinue, onImprove, locked, detailsOpen, onToggleDetails, programName, urgent, dueBy }) => (
  // The CTA stays centred on the card; the urgent flag and the expander are
  // taken out of the flow and pinned to the edges, so a flag on one card
  // doesn't shunt its Continue out of line with the cards above and below it.
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 22,
      flexShrink: 0
    }}
  >
    {/* Urgent flag — the same red "!" the gamification hub's Programs Due chip
        uses, shown only for programs in its Urgent Zone. A permanent due
        date here said nothing on the programs that aren't pressing. */}
    {urgent && (
      <DosenTooltip
        title={`${
          dueBy != null ? `Due ${formatDueBy(dueBy)}. ` : ''
        }This program is in the Urgent Zone of your Programs Due list, in the bar above.`}
        placement="bottom"
      >
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 15,
            height: 15,
            borderRadius: '50%',
            bgcolor: DUE_BY_RED,
            color: '#FDFDFD',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 11,
            fontWeight: 800,
            lineHeight: 1,
            cursor: 'default'
          }}
        >
          !
        </Box>
      </DosenTooltip>
    )}
    <DosenTooltip title={locked ? PROGRAM_LOCKED_TOOLTIP : ''} placement="top">
      {/* No padlock here — the locked state is carried by the name pill's lock
          and the greyed-out CTA, so a second icon beside Continue was noise. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        {onImprove ? (
          <Box
            role="button"
            onClick={onImprove}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 }
            }}
          >
            <Typography
              sx={{
                color: '#FF6A55',
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '16.8px'
              }}
            >
              Retakes
            </Typography>
          </Box>
        ) : (
          <ContinueLink onClick={onContinue} disabled={locked} />
        )}
      </Box>
    </DosenTooltip>
    <DosenTooltip title={detailsOpen ? 'Hide details' : 'Show details'}>
      <IconButton
        onClick={onToggleDetails}
        aria-label={detailsOpen ? `Hide details for ${programName}` : `Show details for ${programName}`}
        // Out of the flow and pinned right so it never pushes the CTA off-centre.
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 22,
          height: 22,
          p: 0,
          color: '#B6B6B6',
          flexShrink: 0
        }}
      >
        {detailsOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    </DosenTooltip>
  </Box>
);

// Phone: the urgent "!" sits in the card's meta line, said in words for screen readers.
const UrgentMark: React.FC = () => (
  <Box
    component="span"
    aria-label="Urgent"
    sx={{
      width: 15,
      height: 15,
      borderRadius: '50%',
      bgcolor: DUE_BY_RED,
      color: '#FDFDFD',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, sans-serif',
      fontSize: 11,
      fontWeight: 800,
      lineHeight: 1,
      flexShrink: 0
    }}
  >
    !
  </Box>
);

// Phone: Retakes in place of Open, as a full-height tap target.
const RetakesLink: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    role="button"
    onClick={onClick}
    sx={{
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 40,
      px: '12px',
      border: '1px solid rgba(255, 106, 85, 0.6)',
      borderRadius: '8px',
      cursor: 'pointer'
    }}
  >
    <Typography
      sx={{ color: '#FF6A55', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, lineHeight: 1 }}
    >
      Retakes
    </Typography>
  </Box>
);

const ContinueLink: React.FC<{ onClick: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <Box
    role="button"
    onClick={disabled ? undefined : onClick}
    sx={{
      // Outline button — bordered, padded, fully-clickable target (matches the
      // admin dashboard). Replaces the old gradient-text link that was hard to
      // see and only clickable dead-on the text.
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      flex: '0 0 auto',
      px: '11px',
      py: '5px',
      border: '1px solid #F79420',
      borderRadius: '6px',
      // Disabled: the house dark state (dim border and text), not an opacity fade.
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...(disabled && { borderColor: '#2A2A2A' }),
      transition: 'background-color 0.15s',
      '&:hover': disabled ? undefined : { backgroundColor: 'rgba(247, 148, 32, 0.12)' },
      // Phone: a thumb-sized target.
      [MOBILE_SX]: { minHeight: 40, px: '16px', borderRadius: '8px' }
    }}
  >
    <Typography
      sx={{
        color: disabled ? '#565656' : '#F79420',
        fontFamily: 'Inter, sans-serif',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '16.8px'
      }}
    >
      Open
    </Typography>
  </Box>
);

// Progress row — bar + percent + info tooltip. Same Figma spec as Admin
// but the tooltip copy is member-scoped ("Your progress on this program").
const ProgressRow: React.FC<{ rate: number }> = ({ rate }) => {
  const clamped = Math.max(0, Math.min(100, rate));
  const fill = getProgressFill(clamped);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Box sx={{ flex: '1 1 0', height: 8, bgcolor: PROGRESS_TRACK_BG, borderRadius: '4px', overflow: 'hidden' }}>
        <Box sx={{ width: `${clamped}%`, height: '100%', bgcolor: fill }} />
      </Box>
      <Typography
        sx={{
          color: '#868686',
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          fontWeight: 400,
          textTransform: 'uppercase',
          lineHeight: 1
        }}
      >
        {clamped}%
      </Typography>
      <InfoTip title={PROGRAM_COMPLETION_TOOLTIP} />
    </Box>
  );
};

// Field row — fixed 145px label + flex-1 left-aligned value + optional
// trailing slot (used by Mentorship/Guided layouts to dock the role toggle
// on the Model row). Width sized to fit the longest label across all
// layouts ("Meetings Completed:") at Inter 14/400; nowrap on both pieces
// guarantees rows never break onto a second line. Both label and value
// are Inter 14/400; value colour overrides for Not Started (amber) and
// Due By red flag.
const FieldRow: React.FC<{
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  trailing?: React.ReactNode;
}> = ({ label, value, valueColor, trailing }) => {
  // The label used to hold a fixed 145px column. In a pillar column the card
  // is around 250px wide, so the label took most of it and left the value
  // roughly 65px: "Course" read "Co...", "Not Started" read "In P...", every
  // date read "Sep...". The label now takes only the width of its own text and
  // the value gets whatever is left, which is enough for all of them.
  //
  // A value can still be long enough to clip (a distant date, a long owner
  // name), so anything textual carries its full text in a tooltip.
  const tooltip = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return (
    <DosenTooltip title={tooltip} placement="bottom" componentsProps={{ tooltip: { sx: { pointerEvents: 'none' } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, cursor: 'default' }}>
        <Typography
          sx={{
            flex: '0 0 auto',
            whiteSpace: 'nowrap',
            color: '#868686',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '16.8px'
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            textAlign: 'right',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: valueColor ?? '#B6B6B6',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '16.8px',
            // Phone: the value wraps whole (there's no hover to read a cut one).
            [MOBILE_SX]: { whiteSpace: 'normal', overflow: 'visible', overflowWrap: 'anywhere' }
          }}
        >
          {value}
        </Box>
        {trailing}
      </Box>
    </DosenTooltip>
  );
};

// Mentor/Learner role chip — Figma: segmented control, radius 4, overflow
// hidden. Selected segment bg #444444 with white 10/500 text; default
// bg #2A2A2A with #B6B6B6 10/400 text. Always rendered on Mentorship
// cards:
//   • dual-role → two interactive segments (Learner left, Mentor right)
//   • single-role → one static chip in the right-anchored position;
//     the absent role is not rendered (no empty slot on the left)
const MentorshipToggle: React.FC<{
  roles: MemberRoleKey[];
  value: MemberRoleKey;
  onChange: (next: MemberRoleKey) => void;
}> = ({ roles, value, onChange }) => {
  const w = useMemberWord();
  const hasMentee = roles.includes('mentee');
  const hasMentor = roles.includes('mentor');
  const interactive = hasMentee && hasMentor;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '4px',
        overflow: 'hidden',
        flex: '0 0 auto'
      }}
      role={interactive ? 'group' : undefined}
      aria-label={interactive ? `${w('mentor')} or Learner view` : undefined}
    >
      {hasMentee && (
        <ToggleSegment
          selected={value === 'mentee'}
          label="Learner"
          interactive={interactive}
          onClick={() => onChange('mentee')}
        />
      )}
      {hasMentor && (
        <ToggleSegment
          selected={value === 'mentor'}
          label={w('mentor')}
          interactive={interactive}
          onClick={() => onChange('mentor')}
        />
      )}
    </Box>
  );
};

const ToggleSegment: React.FC<{
  selected: boolean;
  label: string;
  interactive: boolean;
  onClick: () => void;
}> = ({ selected, label, interactive, onClick }) => (
  <Box
    component={interactive ? 'button' : 'span'}
    type={interactive ? 'button' : undefined}
    onClick={interactive ? onClick : undefined}
    aria-pressed={interactive ? selected : undefined}
    sx={{
      px: '8px',
      py: '4px',
      bgcolor: selected ? '#444444' : '#2A2A2A',
      color: selected ? '#FDFDFD' : '#B6B6B6',
      fontFamily: 'Inter, sans-serif',
      fontSize: 10,
      fontWeight: selected ? 500 : 400,
      lineHeight: 1.2,
      border: 'none',
      cursor: interactive ? 'pointer' : 'default',
      transition: 'background-color 0.12s, color 0.12s',
      ...(interactive && {
        '&:hover': { bgcolor: selected ? '#4a4a4a' : '#333333' }
      })
    }}
  >
    {label}
  </Box>
);

export default MemberProgramCard;
